import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Box, IconButton } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MessageBase from './MessageBase';
import FormattedText from '../FormattedText';
import LoadingIndicator from './LoadingIndicator';
import DownloadButton from './DownloadButton';
import MediaModal from './MediaModal';
import useDownloadHandler from '../../hooks/useDownloadHandler';

// Cache global para videos ya cargados
const globalVideoCache = new Map();

const VideoMessage = ({ text, sender, timestamp, status, media_url, caption, id }) => {
    // Identificador único para el video
    const videoId = id || `video-${media_url?.substring(media_url?.lastIndexOf('/') + 1) || Date.now()}`;
    
    // Verificar inmediatamente si el video ya está en caché
    const isVideoCached = useMemo(() => globalVideoCache.has(media_url), [media_url]);
    
    // El estado loading comienza como false si el video ya está en caché
    const [loading, setLoading] = useState(!isVideoCached);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    // Referencias para los elementos de video
    const videoRef = useRef(null);
    const thumbnailRef = useRef(null);
    const originalUrlRef = useRef(media_url);
    
    // Usar el hook personalizado para la descarga
    const { isDownloading, handleDownload } = useDownloadHandler(
        media_url, 
        '.mp4', 
        'video', 
        caption
    );
    
    // Efecto para precargar y manejar el video
    useEffect(() => {
        // Si ya está en caché, no hacemos nada
        if (globalVideoCache.has(media_url)) {
            setLoading(false);
            return;
        }
        
        // Verificar si la URL cambió
        if (originalUrlRef.current !== media_url) {
            console.warn('La URL del video cambió para el mismo mensaje:', videoId);
            originalUrlRef.current = media_url;
        }
        
        // Para casos donde media_url es undefined o null
        if (!media_url) {
            setLoading(false);
            return;
        }
        
        // No podemos precargar videos como imágenes, pero podemos marcarlos
        // como "intentados" para evitar múltiples intentos de carga
        const preloadAttempt = setTimeout(() => {
            if (!globalVideoCache.has(media_url)) {
                // Marcar como "en proceso de carga"
                globalVideoCache.set(media_url, "loading");
            }
        }, 100);
        
        return () => {
            clearTimeout(preloadAttempt);
        };
    }, [media_url, videoId]);
    
    // Manejadores para eventos de modal
    const handleOpenModal = () => setIsModalOpen(true);
    
    const handleCloseModal = () => {
        setIsModalOpen(false);
        // Pausa el video cuando se cierra el modal
        if (videoRef.current) {
            videoRef.current.pause();
        }
    };
    
    // Manejadores para eventos de video
    const handleVideoLoad = () => {
        globalVideoCache.set(media_url, "loaded");
        setLoading(false);
    };
    
    const handleVideoError = () => {
        // Incluso en error, marcamos para no volver a intentar
        globalVideoCache.set(media_url, "error");
        setLoading(false);
    };
    
    // Detectar actualizaciones de estado
    useEffect(() => {
        if (status && !loading && globalVideoCache.has(media_url)) {
            setLoading(false);
        }
    }, [status, media_url, loading]);
    
    // Manejador de descarga que previene el evento de click
    const handleDownloadClick = (e) => {
        e.stopPropagation();
        handleDownload();
    };
    
    return (
        <MessageBase sender={sender} timestamp={timestamp} status={status}>
            <Box sx={{ paddingBottom: '15px' }}>
                {/* Contenedor para el video y el indicador de carga */}
                <Box 
                    sx={{
                        position: 'relative',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        minHeight: loading ? '150px' : 'auto',
                        backgroundColor: 'rgba(0,0,0,0.05)'
                    }}
                    onClick={handleOpenModal}
                >
                    {/* Indicador de carga - solo si realmente está cargando */}
                    {loading && (
                        <LoadingIndicator 
                            contentType="video"
                            isLoading={loading}
                            color={sender === 'user' ? 'inherit' : 'primary'}
                            textColor={sender === 'user' ? 'white' : 'text.secondary'}
                        />
                    )}
                    
                    {/* Video miniatura */}
                    {media_url && (
                        <Box
                            component="video"
                            ref={thumbnailRef}
                            src={media_url}
                            onLoadedData={handleVideoLoad}
                            onError={handleVideoError}
                            sx={{
                                width: '100%',
                                maxHeight: '200px',
                                objectFit: 'cover',
                                borderRadius: '10px',
                                opacity: loading ? 0 : 1,
                                transition: 'opacity 0.3s'
                            }}
                        />
                    )}
                    
                    {/* Overlay de reproducción - solo visible cuando no está cargando */}
                    {!loading && media_url && (
                        <Box 
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0,0,0,0.3)',
                                borderRadius: '10px',
                            }}
                        >
                            <IconButton 
                                sx={{ 
                                    color: 'white',
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0,0,0,0.7)',
                                    }
                                }}
                            >
                                <PlayArrowIcon fontSize="large" />
                            </IconButton>
                        </Box>
                    )}
                    
                    {/* Botón de descarga flotante - solo visible cuando no está cargando */}
                    {!loading && media_url && (
                        <Box sx={{
                            position: 'absolute',
                            top: 5,
                            right: 5,
                            display: 'flex',
                            gap: 1,
                            zIndex: 3
                        }}>
                            <DownloadButton 
                                onDownload={handleDownloadClick}
                                isDownloading={isDownloading}
                                fileType="video"
                                size="small"
                            />
                        </Box>
                    )}
                </Box>
                
                {/* Si hay caption, mostrarlo */}
                {caption && (
                    <Box sx={{ mt: 1 }}>
                        <FormattedText text={caption} />
                    </Box>
                )}
                
                {/* Modal para reproducir el video */}
                <MediaModal 
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    caption={caption}
                    loading={loading}
                    isDownloading={isDownloading}
                    onDownload={handleDownload}
                    contentType="video"
                >
                    {media_url && (
                        <Box
                            component="video"
                            ref={videoRef}
                            src={media_url}
                            controls
                            autoPlay
                            onLoadedData={handleVideoLoad}
                            onError={handleVideoError}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                opacity: loading ? 0 : 1,
                                transition: 'opacity 0.3s'
                            }}
                        />
                    )}
                </MediaModal>
            </Box>
        </MessageBase>
    );
};

export default React.memo(VideoMessage);